/**
 * Product Options Price Manager
 *
 * Plugin-registry architecture.  Each option plugin registers a
 * `getAddonCents()` function.  The manager handles everything else.
 *
 * Key invariant — the base price is IMMUTABLE between variant changes.
 * It is NEVER re-read from DOM elements that we ourselves have modified.
 *
 * To add a new plugin in the future:
 *   PriceManager.register({ id, getAddonCents, containerSelector, triggerSelectors })
 */
(function () {
  'use strict';

  var DEBUG = true;
  function log() { if (DEBUG) console.log.apply(console, arguments); }

  /* ═══════════════════════════════════════════════════════════════
     PriceManager
     ═══════════════════════════════════════════════════════════════ */

  var PriceManager = (function () {

    var _registry     = [];
    var _basePrice    = 0;      // immutable between variant changes (cents)
    var _debounce     = null;
    var _observers    = [];
    var _variantUnsub = null;
    var _started      = false;

    /* ── Public API ─────────────────────────────────────────── */
    function register(opts) {
      if (!opts || !opts.id || typeof opts.getAddonCents !== 'function') {
        console.warn('[PriceManager] invalid registration — need id + getAddonCents()');
        return;
      }
      for (var i = 0; i < _registry.length; i++) {
        if (_registry[i].id === opts.id) { return; }
      }
      _registry.push(opts);
      log('[PriceManager] registered "' + opts.id + '"');
    }

    function start() {
      if (_started) return;
      if (_registry.length === 0) return;

      _readFreshBasePrice();       // one-shot read from pristine DOM
      _bindGlobalEvents();
      _bindObservers();
      _bindVariantChange();
      _disableOldAvisBridge();

      _started = true;
      log('[PriceManager] started — base:' + _basePrice + 'c  plugins:' +
          _registry.map(function(r) { return r.id; }).join(', '));

      setTimeout(_recalculate, 300);
    }

    /* ── Base price (immutable) ─────────────────────────────── */
    function _readFreshBasePrice() {
      var bp = document.querySelector('product-buy-price');
      if (bp) {
        var raw = parseInt(bp.getAttribute('data-price'), 10);
        if (!isNaN(raw)) { _basePrice = raw; return; }
      }
      var jsonEl = document.querySelector('variant-selects [data-selected-variant]');
      if (jsonEl) {
        try {
          var v = JSON.parse(jsonEl.textContent);
          if (v && typeof v.price === 'number') { _basePrice = v.price; return; }
        } catch (_) {}
      }
      log('[PriceManager] WARN: could not read base price');
    }

    /* ── Recalculate ────────────────────────────────────────── */
    function _recalculate() {
      // NEVER re-read base price from DOM here — it's immutable.
      // Base price only changes via variantChange event.

      var addon = 0;
      var parts = [];
      for (var i = 0; i < _registry.length; i++) {
        try {
          var a = _registry[i].getAddonCents();
          if (typeof a === 'number') {
            addon += a;
            parts.push(_registry[i].id + ':' + a + 'c');
          }
        } catch (e) {
          parts.push(_registry[i].id + ':ERR');
        }
      }

      var combined = _basePrice + addon;
      if (combined < 0) combined = 0;

      log('[PriceManager] base:' + _basePrice + 'c  ' + parts.join('  ') +
          '  → ' + combined + 'c  (' + (combined / 100).toFixed(2) + ' €)');

      var fmt = theme.settings.currencyCodeEnabled
        ? theme.settings.moneyWithCurrencyFormat
        : theme.settings.moneyFormat;
      var html = theme.Currency.formatMoney(combined, fmt);

      _updateDOM(combined, html);
    }

    function _updateDOM(cents, html) {
      // Main price  (preserve the sale/comparison markup — only update .price__regular)
      var pw = document.querySelector('[id^="Price-"]');
      if (pw && !pw.querySelector('price-per-item')) {
        var reg = pw.querySelector('.price__regular');
        if (reg) reg.innerHTML = html;
      }

      // Sticky bar
      var sp = document.querySelector('[id^="StickyPrice-"]');
      if (sp && !sp.querySelector('price-per-item')) {
        var reg = sp.querySelector('.price__regular');
        if (reg) reg.innerHTML = html;
      }

      // Buy button(s) — update both data-price AND display
      var allBp = document.querySelectorAll('product-buy-price');
      for (var i = 0; i < allBp.length; i++) {
        if (allBp[i].isConnected) {
          allBp[i].setAttribute('data-price', cents);
          allBp[i].innerHTML = html;
        }
      }
    }

    function _schedule(ms) {
      clearTimeout(_debounce);
      _debounce = setTimeout(_recalculate, ms || 80);
    }

    /* ── Events ─────────────────────────────────────────────── */
    function _bindGlobalEvents() {
      // Collect all trigger selectors
      var selectors = [];
      for (var i = 0; i < _registry.length; i++) {
        var ts = _registry[i].triggerSelectors;
        if (Array.isArray(ts)) {
          for (var j = 0; j < ts.length; j++) {
            if (selectors.indexOf(ts[j]) === -1) selectors.push(ts[j]);
          }
        }
      }

      // click — any registered trigger
      document.addEventListener('click', function (e) {
        var t = e.target;
        if (!t || !t.closest) return;
        for (var i = 0; i < selectors.length; i++) {
          if (t.closest(selectors[i])) { _schedule(200); return; }
        }
        // Also fire for any click inside a registered container
        for (var j = 0; j < _registry.length; j++) {
          var cs = _registry[j].containerSelector;
          if (cs && t.closest(cs)) { _schedule(200); return; }
        }
      }, true);

      // change — inside any registered container
      document.addEventListener('change', function (e) {
        var t = e.target;
        if (!t || !t.closest) return;
        for (var i = 0; i < _registry.length; i++) {
          var cs = _registry[i].containerSelector;
          if (cs && t.closest(cs)) { _schedule(100); return; }
        }
      }, true);

      log('[PriceManager] events bound (' + selectors.length + ' selectors)');
    }

    /* ── MutationObservers ──────────────────────────────────── */
    function _bindObservers() {
      for (var i = 0; i < _registry.length; i++) {
        _attachObserver(_registry[i]);
      }
      log('[PriceManager] ' + _observers.length + ' observer(s) active');
    }

    function _attachObserver(reg) {
      var root = document.querySelector(reg.containerSelector);
      if (!root) {
        // Retry later (plugin may render asynchronously)
        var tries = 0;
        var t = setInterval(function () {
          tries++;
          root = document.querySelector(reg.containerSelector);
          if (root) {
            clearInterval(t);
            _startObserving(root, reg.id);
          } else if (tries > 60) { clearInterval(t); }
        }, 200);
        return;
      }
      _startObserving(root, reg.id);
    }

    function _startObserving(root, label) {
      var target = root.shadowRoot || root;
      var obs = new MutationObserver(function () { _schedule(80); });
      obs.observe(target, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: [
          'checked', 'selected',
          'ymq-option-price', 'ymq-option-checked', 'data-ymq-money',
          'data-variant-price',
          'class', 'style'
        ]
      });
      _observers.push(obs);
      log('[PriceManager] observer on "' + label + '"');
    }

    /* ── Variant change ─────────────────────────────────────── */
    function _bindVariantChange() {
      _variantUnsub = theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.variantChange,
        function (event) {
          if (!event || !event.data || !event.data.variant) return;
          // New variant → new base price from the event (trusted source, in cents)
          _basePrice = event.data.variant.price;
          log('[PriceManager] variantChange → base:' + _basePrice + 'c');
          // DOM is about to be replaced.  Wait, then rebind observers.
          setTimeout(function () {
            _readFreshBasePrice();
            // Re-attach observers (old DOM elements are gone)
            _observers.forEach(function(o) { o.disconnect(); });
            _observers = [];
            for (var i = 0; i < _registry.length; i++) {
              _attachObserver(_registry[i]);
            }
            _disableOldAvisBridge();
            _recalculate();
          }, 400);
        }
      );
    }

    /* ── Disable old avis bridge ────────────────────────────── */
    function _disableOldAvisBridge() {
      if (window.__avisPriceBridge && typeof window.__avisPriceBridge.teardown === 'function') {
        try {
          window.__avisPriceBridge.teardown();
          log('[PriceManager] disabled old avis bridge');
        } catch (e) {
          log('[PriceManager] WARN: ' + e.message);
        }
      }
    }

    return { register: register, start: start, _registry: _registry };

  })();  // end PriceManager


  /* ═══════════════════════════════════════════════════════════════
     Plugin: YMQ Product Options
     ═══════════════════════════════════════════════════════════════ */

  PriceManager.register({
    id: 'ymq',
    getAddonCents: function () {
      var euros = 0;

      var boxes = document.querySelectorAll('.ymq-options-box');
      if (boxes.length === 0) {
        var ymqEl = document.querySelector('ymq-option');
        if (ymqEl && ymqEl.shadowRoot) {
          boxes = ymqEl.shadowRoot.querySelectorAll('.ymq-options-box');
        }
      }

      for (var i = 0; i < boxes.length; i++) {
        var box = boxes[i];

        // Radio / checkbox — YMQ sets ymq-option-checked="true"
        var inputs = box.querySelectorAll(
          'input[ymq-option-price][ymq-option-checked="true"], ' +
          'input[ymq-option-price]:checked'
        );
        for (var j = 0; j < inputs.length; j++) {
          euros += _p(inputs[j].getAttribute('ymq-option-price'));
        }

        // <select>
        var selects = box.querySelectorAll('select');
        for (var k = 0; k < selects.length; k++) {
          if (selects[k].selectedOptions && selects[k].selectedOptions.length) {
            euros += _p(selects[k].selectedOptions[0].getAttribute('ymq-option-price'));
          }
        }

        // <option ymq-option-checked="true"> (non-select dropdowns)
        var opts = box.querySelectorAll('option[ymq-option-price][ymq-option-checked="true"]');
        for (var m = 0; m < opts.length; m++) {
          var p = opts[m].parentElement;
          if (p && p.tagName === 'SELECT' && p.selectedOptions &&
              p.selectedOptions.length && p.selectedOptions[0] === opts[m]) {
            continue;
          }
          euros += _p(opts[m].getAttribute('ymq-option-price'));
        }
      }

      return Math.round(euros * 100);  // euros → cents
    },
    containerSelector: 'ymq-option',
    triggerSelectors: [
      '.ymq-option-value-item',
      '.ymq-option-label',
      '.ymq-option-button-content'
    ]
  });


  /* ═══════════════════════════════════════════════════════════════
     Plugin: Avis Product Options
     ═══════════════════════════════════════════════════════════════ */

  PriceManager.register({
    id: 'avis',
    getAddonCents: function () {
      var total = 0;
      var c = document.getElementById('avpoptions-container__v2');
      if (!c) return 0;

      var cbs = c.querySelectorAll(
        'input.avp-productoptiontextcolor[type="checkbox"]:checked'
      );
      for (var i = 0; i < cbs.length; i++) {
        var cb = cbs[i];
        var price = parseInt(cb.getAttribute('data-variant-price'), 10);
        if (isNaN(price)) continue;

        var qty = 1;
        var grid = cb.closest('.avp-value-grid');
        if (grid) {
          var qi = grid.querySelector('.avp-qty-input');
          if (qi) {
            var v = parseInt(qi.value, 10);
            if (!isNaN(v) && v > 0) qty = v;
          }
        }
        total += price * qty;
      }
      return total; // already in cents
    },
    containerSelector: '#avpoptions-container__v2',
    triggerSelectors: [
      '.avp-productoptioncheckwrapper',
      '.avp-qty-button'
    ]
  });


  /* ═══════════════════════════════════════════════════════════════
     Bootstrap
     ═══════════════════════════════════════════════════════════════ */

  function whenReady(fn) {
    if (typeof theme !== 'undefined' && theme.Currency && theme.pubsub) {
      fn(); return;
    }
    var t = setInterval(function () {
      if (typeof theme !== 'undefined' && theme.Currency && theme.pubsub) {
        clearInterval(t); fn();
      }
    }, 100);
  }

  whenReady(function () {
    var tries = 0;
    var t = setInterval(function () {
      tries++;
      for (var i = 0; i < PriceManager._registry.length; i++) {
        var cs = PriceManager._registry[i].containerSelector;
        if (cs && document.querySelector(cs)) {
          clearInterval(t);
          PriceManager.start();
          return;
        }
      }
      if (tries > 60) { clearInterval(t); }
    }, 200);
  });

  function _p(val) {
    if (!val) return 0;
    var n = parseFloat(val);
    return isNaN(n) ? 0 : n;
  }

})();
